import React from "react";

import { Home2026ExpertiseBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Cta } from "../../shared/Cta";
import { Mask, useReveal } from "../../shared/Reveal";
import { SectionHeader } from "../../shared/SectionHeader";
import { usePrefersReducedMotion } from "../../shared/reducedMotion";
import { Cover } from "./Cover";
import { LoopGraphic } from "./LoopGraphic";

import styles from "./index.module.scss";

/** expertise.js: the CTA rides the circles gate, 760ms behind it */
const CTA_DELAY = 760;

/**
 * The 2026 homepage's Expertise section: a centred header, the four loops
 * opening out of the centre line with an area's copy arriving as each one
 * passes halfway, the halo under them, and the CTA.
 */
export const Home2026ExpertiseBlock = (props: Value) => {
    const sectionRef = React.useRef<HTMLElement>(null);
    const reduced = usePrefersReducedMotion();
    const [inView, setInView] = React.useState(false);
    // Rendered open, so the server markup (and no-JS) shows every area
    const [opened, setOpened] = React.useState(4);

    // The circles gate measures the loops' own band, not the graphic's box.
    // Rooted on the areas, so focus landing on the CTA opens it.
    const { ref: areasRef, shown } = useReveal<HTMLDivElement>({
        enter: 0.92,
        exit: 1.05,
        delays: [0, CTA_DELAY],
        target: (root) => root.querySelector(`.${styles.band}`),
    });
    const [loopsShown, ctaShown] = shown;

    React.useEffect(() => {
        const section = sectionRef.current;
        if (!section) return;
        const observer = new IntersectionObserver(
            (entries) => setInView(entries[entries.length - 1].isIntersecting),
            { threshold: 0 },
        );
        observer.observe(section);
        return () => observer.disconnect();
    }, []);

    const cta = props.cta;

    return (
        <section ref={sectionRef} className={styles.root}>
            <Cover
                sectionRef={sectionRef}
                headerSelector={`.${styles.header}`}
            />
            <div className={styles.inner}>
                {props.header && (
                    <SectionHeader
                        value={props.header}
                        align="center"
                        sequence={{ eyebrow: 0, title: 110, body: 330 }}
                        className={styles.header}
                    />
                )}
                <div ref={areasRef} className={styles.areas}>
                    <LoopGraphic
                        open={loopsShown}
                        inView={inView}
                        reduced={reduced}
                        onOpened={setOpened}
                    />
                    <span className={styles.band} aria-hidden="true" />
                    <div className={styles.stage}>
                        <div className={styles.row}>
                            {props.areas?.map(({ id, value }, i) => (
                                <div key={id} className={styles.area}>
                                    {value.heading && (
                                        <h3 className={styles.title}>
                                            <Mask shown={opened > i}>
                                                {value.heading}
                                            </Mask>
                                        </h3>
                                    )}
                                    {value.body && (
                                        <div className={styles.desc}>
                                            <RichTextBlock
                                                value={value.body}
                                                className={concatClassNames([
                                                    styles.descText,
                                                    opened > i
                                                        ? ""
                                                        : styles.pre,
                                                ])}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {cta?.url && cta.display_name && (
                            <div className={styles.cta}>
                                <Mask cta shown={ctaShown}>
                                    <Cta variant="dark" link={cta}>
                                        {cta.display_name}
                                    </Cta>
                                </Mask>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
